import type { ZodType } from 'zod'

import { zodIssueWrapper } from '@img-arena/consoles-utils'
import { getQueryParams } from '@/utils/api/getQueryParams'

import { HTTP_STATUS_CODE } from './constants/httpStatusCode'
import { API_METHODS, DEFAULT_REQUEST_OPTIONS } from './constants/requests'
import { ApiError } from './ApiError'
import { logout } from './authService'
import { getHeaders } from './getHeaders'

type Parsable = { safeParse: ZodType['safeParse'] }

export type EndpointData<Schema extends Parsable = Parsable> = {
  url: string | null
  parser?: Schema | null
} | null

type RequestDataOptions<Data = unknown> = {
  endpointData: EndpointData
  data?: Data
  noHeaders?: boolean
  options?: Partial<RequestInit>
}

type RequestOptions<Data> = RequestDataOptions<Data> & {
  method: API_METHODS
}

async function request<Data = unknown, ResponseBody = unknown>({
  endpointData,
  method,
  data,
  options,
  noHeaders
}: RequestOptions<Data>): Promise<ResponseBody> {
  if (!endpointData || !endpointData.url) return Promise.reject(new Error('Missing url'))

  const headers = getHeaders(options && options.headers)
  let url = endpointData.url
  const finalOptions: RequestInit = {
    ...DEFAULT_REQUEST_OPTIONS,
    method,
    ...options
  }

  if (!noHeaders) {
    finalOptions.headers = headers
  }

  if (data && method !== API_METHODS.GET) {
    finalOptions.body = JSON.stringify(data)
  } else if (data && method === API_METHODS.GET) {
    url += `?${getQueryParams(data)}`
  }

  const response = await fetch(url, finalOptions)

  if (!response.ok) {
    const error = await ApiError.parseFromResponse(response)

    if (error.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
      logout()
    }

    throw error
  }

  const contentType = response.headers.get('content-type')
  const isJson = contentType && contentType === 'application/json'
  const responseData: ResponseBody = isJson ? await response.json() : await response.text()

  if (!endpointData.parser) {
    return responseData
  }

  let parsedData = responseData

  if (typeof responseData === 'string') {
    try {
      parsedData = JSON.parse(responseData)
    } catch (error) {
      parsedData = responseData
    }
  }

  const parsedResponse = endpointData.parser.safeParse(parsedData)

  if (!parsedResponse.success) {
    throw Error(zodIssueWrapper(parsedResponse.error, endpointData.url))
  }

  return responseData
}

export async function postData<Data = unknown, ResponseBody = unknown>({
  endpointData,
  data,
  options
}: RequestDataOptions<Data>): Promise<ResponseBody> {
  return request<Data, ResponseBody>({ endpointData, method: API_METHODS.POST, data, options })
}

export async function putData<Data = unknown, ResponseBody = unknown>({
  endpointData,
  data,
  options
}: RequestDataOptions<Data>): Promise<ResponseBody> {
  return request<Data, ResponseBody>({ endpointData, method: API_METHODS.PUT, data, options })
}

export async function patchData<Data = unknown, ResponseBody = unknown>({
  endpointData,
  data,
  options
}: RequestDataOptions<Partial<Data>>): Promise<ResponseBody> {
  return request<Partial<Data>, ResponseBody>({
    endpointData,
    method: API_METHODS.PATCH,
    data,
    options
  })
}

export async function fetchData<ResponseBody = unknown, Data = unknown>({
  endpointData,
  data,
  options,
  noHeaders
}: RequestDataOptions<Data>): Promise<ResponseBody> {
  if (noHeaders) {
    return request<Data, ResponseBody>({ endpointData, method: API_METHODS.GET, noHeaders, data })
  }

  return request<Data, ResponseBody>({ endpointData, method: API_METHODS.GET, options, data })
}

export async function deleteData<Data = unknown>({
  endpointData,
  options,
  data
}: RequestDataOptions<Data>): Promise<void> {
  return request<Data, void>({ endpointData, method: API_METHODS.DELETE, data, options })
}
